/**
 * QuarnX WebSocket Client
 *
 * Connects to the kernel WebSocket endpoint at /ws for real-time
 * block and transaction updates. Implements exponential backoff
 * reconnection (1s, 2s, 4s, 8s, 16s, 30s cap) and emits
 * connection-status events to the Pinia store.
 *
 * Message format from kernel:
 *   { type: "new_block", data: { slot, hash, validator, tx_count, gas_used, timestamp } }
 *   { type: "new_transaction", data: { hash, from, to, value, gas_used, status } }
 */

/** Maximum reconnection delay in milliseconds */
const MAX_RECONNECT_DELAY_MS = 30000

/**
 * Derives the WebSocket URL from the API base URL.
 * Converts http(s)://host to ws(s)://host/ws
 * Forces WSS on production (non-localhost) environments.
 */
function deriveWsUrl() {
  const apiBase = window.__QUARNX_API_URL__ || import.meta.env.VITE_KERNEL_API || ''
  if (!apiBase || apiBase === '') {
    // Derivar do hostname atual (produção)
    const proto = window.location.protocol === 'https:' ? 'wss' : 'ws'
    return `${proto}://${window.location.host}/ws`
  }
  const isLocal = apiBase.includes('localhost') || apiBase.includes('127.0.0.1')
  const wsProtocol = isLocal ? 'ws' : 'wss'
  const hostPart = apiBase.replace(/^https?:\/\//, '')
  return `${wsProtocol}://${hostPart}/ws`
}

/** Default WebSocket URL (derived from API config) */
const DEFAULT_WS_URL = deriveWsUrl()

/**
 * Calculates exponential backoff delay for a given attempt number.
 * Formula: min(2^attempt * 1000, 30000) ms
 *
 * @param {number} attempt - Zero-based attempt counter
 * @returns {number} Delay in milliseconds
 */
export function calculateBackoff(attempt) {
  return Math.min(Math.pow(2, attempt) * 1000, MAX_RECONNECT_DELAY_MS)
}

/**
 * WebSocket client for the QuarnX Block Explorer.
 *
 * Usage:
 *   const ws = new QuarnXWebSocket('ws://localhost:8080/ws')
 *   ws.onMessage((msg) => console.log(msg))
 *   ws.connect()
 */
export class QuarnXWebSocket {
  /**
   * @param {string} [url] - WebSocket endpoint URL
   */
  constructor(url = DEFAULT_WS_URL) {
    /** @type {string} */
    this._url = url

    /** @type {WebSocket|null} */
    this._socket = null

    /** @type {Function[]} Message handler callbacks */
    this._messageHandlers = []

    /** @type {Function[]} Connection status change callbacks */
    this._statusHandlers = []

    /** @type {boolean} Whether the client is intentionally disconnected */
    this._intentionalClose = false

    /** @type {number} Consecutive reconnection attempts */
    this._reconnectAttempts = 0

    /** @type {number|null} Reconnect timer ID */
    this._reconnectTimer = null

    /** @type {boolean} Current connection state */
    this._connected = false
  }

  /**
   * Establishes a WebSocket connection to the kernel.
   * If already connected, this is a no-op.
   */
  connect() {
    if (this._socket && this._socket.readyState === WebSocket.OPEN) {
      return
    }

    this._intentionalClose = false
    this._createConnection()
  }

  /**
   * Gracefully closes the WebSocket connection.
   * Prevents automatic reconnection.
   */
  disconnect() {
    this._intentionalClose = true
    this._clearReconnectTimer()
    this._reconnectAttempts = 0

    if (this._socket) {
      this._socket.close()
      this._socket = null
    }

    this._setConnected(false)
  }

  /**
   * Registers a callback for incoming WebSocket messages.
   * The callback receives the parsed JSON message object.
   *
   * @param {Function} callback - Handler receiving { type, data }
   * @returns {Function} Unsubscribe function
   */
  onMessage(callback) {
    if (typeof callback !== 'function') {
      return () => {}
    }
    this._messageHandlers.push(callback)
    return () => {
      this._messageHandlers = this._messageHandlers.filter(h => h !== callback)
    }
  }

  /**
   * Registers a callback for connection status changes.
   * The callback receives a boolean (true = connected, false = disconnected).
   *
   * @param {Function} callback - Handler receiving boolean status
   * @returns {Function} Unsubscribe function
   */
  onStatusChange(callback) {
    if (typeof callback !== 'function') {
      return () => {}
    }
    this._statusHandlers.push(callback)
    // Immediately notify with current state
    callback(this._connected)
    return () => {
      this._statusHandlers = this._statusHandlers.filter(h => h !== callback)
    }
  }

  /**
   * Returns whether the WebSocket is currently connected.
   *
   * @returns {boolean}
   */
  isConnected() {
    return this._connected
  }

  /**
   * Returns the current reconnection attempt count.
   * Useful for testing and debugging.
   *
   * @returns {number}
   */
  getReconnectAttempts() {
    return this._reconnectAttempts
  }

  // --- Private methods ---

  /**
   * Creates the underlying WebSocket and wires up event handlers.
   * @private
   */
  _createConnection() {
    try {
      this._socket = new WebSocket(this._url)
    } catch (err) {
      console.error('[QuarnXWebSocket] Failed to create WebSocket:', err)
      this._scheduleReconnect()
      return
    }

    this._socket.onopen = () => {
      console.info('[QuarnXWebSocket] Connected to', this._url)
      this._reconnectAttempts = 0
      this._setConnected(true)
    }

    this._socket.onclose = (event) => {
      console.warn('[QuarnXWebSocket] Connection closed', {
        code: event.code,
        reason: event.reason,
        wasClean: event.wasClean
      })
      this._setConnected(false)

      if (!this._intentionalClose) {
        this._scheduleReconnect()
      }
    }

    this._socket.onerror = (error) => {
      console.error('[QuarnXWebSocket] Error:', error)
      // onclose will fire after onerror, so reconnect is handled there
    }

    this._socket.onmessage = (event) => {
      this._handleMessage(event)
    }
  }

  /**
   * Parses and dispatches an incoming WebSocket message to all registered handlers.
   * @param {MessageEvent} event
   * @private
   */
  _handleMessage(event) {
    try {
      const message = JSON.parse(event.data)
      this._messageHandlers.forEach(handler => {
        try {
          handler(message)
        } catch (err) {
          console.error('[QuarnXWebSocket] Error in message handler:', err)
        }
      })
    } catch (err) {
      console.error('[QuarnXWebSocket] Failed to parse message:', event.data)
    }
  }

  /**
   * Schedules a reconnection attempt using exponential backoff.
   * @private
   */
  _scheduleReconnect() {
    this._clearReconnectTimer()

    const delay = calculateBackoff(this._reconnectAttempts)
    console.info(
      `[QuarnXWebSocket] Reconnecting in ${delay}ms (attempt ${this._reconnectAttempts + 1})`
    )

    this._reconnectTimer = setTimeout(() => {
      this._reconnectAttempts++
      this._createConnection()
    }, delay)
  }

  /**
   * Clears any pending reconnection timer.
   * @private
   */
  _clearReconnectTimer() {
    if (this._reconnectTimer !== null) {
      clearTimeout(this._reconnectTimer)
      this._reconnectTimer = null
    }
  }

  /**
   * Updates the internal connected state and notifies all status handlers.
   * @param {boolean} connected
   * @private
   */
  _setConnected(connected) {
    if (this._connected === connected) return
    this._connected = connected
    this._statusHandlers.forEach(handler => {
      try {
        handler(connected)
      } catch (err) {
        console.error('[QuarnXWebSocket] Error in status handler:', err)
      }
    })
  }
}

/**
 * Singleton instance for the Block Explorer.
 * Import and use this directly in stores/components.
 */
const wsClient = new QuarnXWebSocket(DEFAULT_WS_URL)

export default wsClient
